import React, { useEffect } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";

import MainLayout from "../components/layout/MainLayout";
import Breadcrumbs from "../components/common/Breadcrumbs";
import FamilyMeetingMinutesList from "./Phase-05/FamilyMeetingMinutes/FamilyMeetingMinutesList";
import {
    fetchFamilyMeetingMinutes,
    deleteFamilyMeetingMinute,
    clearError,
} from "../store/slices/familyMeetingMinutesSlice";
import { selectHasPermission } from "../store/slices/authSlice";

const FamilyMeetingsMinutes = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const canManage = useSelector((state) =>
        selectHasPermission(state, "family-meeting-manage")
    );
    const canCreate = useSelector((state) =>
        selectHasPermission(state, "family-meeting-create")
    );
    const canEdit = useSelector((state) =>
        selectHasPermission(state, "family-meeting-edit")
    );
    const canDelete = useSelector((state) =>
        selectHasPermission(state, "family-meeting-delete")
    );

    const { familyMeetingMinutes, isLoading, error } = useSelector(
        (state) => state.familyMeetingMinutes
    );

    useEffect(() => {
        dispatch(fetchFamilyMeetingMinutes());
    }, [dispatch]);

    const handleAdd = () => navigate("/phase05/family-meeting-minutes/add");
    const handleEdit = (id) =>
        navigate(`/phase05/family-meeting-minutes/edit/${id}`);

    const handleDelete = async (id) => {
        const result = await Swal.fire({
            title: "Are you sure?",
            text: "This Family Meeting Minutes record will be deleted permanently!",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#d33",
            confirmButtonText: "Yes, delete it!",
        });

        if (result.isConfirmed) {
            dispatch(deleteFamilyMeetingMinute(id))
                .unwrap()
                .then(() => {
                    Swal.fire("Deleted!", "Record has been removed successfully.", "success");
                })
                .catch(() => {
                    Swal.fire("Error", "Failed to delete record.", "error");
                });
        }
    };

    if (!canManage)
        return (
            <Navigate
                to="/dashboard"
                state={{
                    error: "You do not have permission to manage Family Meeting Minutes.",
                }}
                replace
            />
        );

    return (
        <MainLayout>
            <div className="container-xxl flex-grow-1 container-p-y">
                <Breadcrumbs />

                {error && (
                    <div
                        className="alert alert-danger alert-dismissible fade show"
                        role="alert"
                    >
                        <strong>Error:</strong> {typeof error === "string" ? error : "An error occurred"}
                        <button
                            type="button"
                            className="btn-close"
                            onClick={() => dispatch(clearError())}
                        ></button>
                    </div>
                )}

                <div className="card">
                    <div className="card-body">
                        {isLoading ? (
                            <div className="d-flex justify-content-center py-5">
                                <div className="spinner-border" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                            </div>
                        ) : (
                            <FamilyMeetingMinutesList
                                records={familyMeetingMinutes}
                                onAdd={canCreate ? handleAdd : null}
                                onEdit={canEdit ? handleEdit : null}
                                onDelete={canDelete ? handleDelete : null}
                            />
                        )}
                    </div>
                </div>
            </div>
        </MainLayout>
    );
};

export default FamilyMeetingsMinutes;
